import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Switch } from "@/components/ui/Switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { useCreateSite, useSites } from "@/hooks/useSites";

export default function SitesPage(): JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: sites, isLoading, error } = useSites();
  const createMut = useCreateSite();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  function reset(): void {
    setName("");
    setDescription("");
    setIsActive(true);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    try {
      await createMut.mutateAsync({
        name,
        description: description || null,
        is_active: isActive,
      });
      toast({ title: "Сайт створено", variant: "success" });
      setOpen(false);
      reset();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Не вдалося створити сайт.";
      toast({ title: "Помилка", description: detail, variant: "destructive" });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Сайти</h1>
          <p className="text-sm text-muted-foreground">
            Логічне групування URL для моніторингу.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Додати сайт
        </Button>
      </div>

      {error ? (
        <p className="text-sm text-destructive">
          Не вдалося завантажити сайти.
        </p>
      ) : null}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Назва</TableHead>
              <TableHead>Опис</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Інциденти</TableHead>
              <TableHead>Статус</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Завантаження…
                </TableCell>
              </TableRow>
            ) : !sites || sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Сайтів ще немає.
                </TableCell>
              </TableRow>
            ) : (
              sites.map((s) => (
                <TableRow
                  key={s.id}
                  onClick={() => navigate(`/sites/${s.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="max-w-md truncate text-muted-foreground">
                    {s.description ?? "—"}
                  </TableCell>
                  <TableCell>{s.urls_count ?? "—"}</TableCell>
                  <TableCell>{s.open_incidents_count ?? 0}</TableCell>
                  <TableCell>
                    {s.is_active ? (
                      <Badge variant="success">Активний</Badge>
                    ) : (
                      <Badge variant="secondary">Вимкнено</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Додати сайт</DialogTitle>
            <DialogDescription>
              Створіть новий запис сайту для подальшого додавання URL.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="site-name">Назва</Label>
              <Input
                id="site-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site-desc">Опис</Label>
              <Textarea
                id="site-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="site-active">Активний</Label>
                <p className="text-xs text-muted-foreground">
                  Якщо вимкнено — перевірки сайту не виконуються.
                </p>
              </div>
              <Switch
                id="site-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Скасувати
              </Button>
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Збереження…
                  </>
                ) : (
                  "Створити"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
